import React, { useState, useEffect } from 'react';

import { withTheme } from 'styled-components';

import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';

import { Container, Header, ItemsPreview, Separator, ItemsContainer, Item, Footer, ErrorMessage } from './styles';

export type PreviewIcons =
  | 'flight'
  | 'hotel'
  | 'policy'
  | 'primary-approval'
  | 'secondary-approval'
  | 'user'
  | 'group'
  | 'auto';

export interface PreviewItem {
  title?: string;
  subtitle?: string;
  iconType?: 'initials' | PreviewIcons;
  disabled?: boolean;
}

export interface Props {
  placeholder: string;
  icon: PreviewIcons;
  showInitials?: boolean;
  previewItems?: PreviewItem[];
  multipleColumns?: boolean;
  noBorder?: boolean;
  onClick: () => void;
  onAdd: () => void;
  theme?: any;
  children?: JSX.Element;
  errorMessage?: string;
}

interface ItemIconProps {
  item: PreviewItem;
  showInitials?: boolean;
  randomBackground: string[];
  index: number;
}

const MAX_ITEMS_DESKTOP_MULTIPLE_COLUMN = 6;
const MAX_ITEMS_MOBILE = 3;

const getInitials = (title?: string) => {
  const splitted = title ? title.split(' ') : [];
  if (splitted.length > 0) {
    return splitted.length === 1
      ? splitted?.first().substring(0, 1)
      : `${splitted.first().substring(0, 1)}${splitted.last().substring(0, 1)}`;
  } else {
    return;
  }
};

const ItemIcon = ({ item, showInitials, randomBackground, index }: ItemIconProps) => {
  if (item.iconType && item.iconType !== 'initials')
    return <Item.ImageIcon src={`/corporate/images/list-preview-icons/${item.iconType}.svg`} alt="" />;
  if (showInitials || item.iconType === 'initials')
    return <Item.Icon $background={randomBackground[index]}>{getInitials(item.title)}</Item.Icon>;
  return null;
};

const ListPreviewCard = ({
  placeholder,
  icon,
  showInitials,
  previewItems,
  multipleColumns,
  noBorder,
  onClick,
  onAdd,
  theme,
  children,
  errorMessage,
}: Props) => {
  const { t } = useStoreTranslation();
  const isDesktop = useIsDesktop();

  const displayMaximum = multipleColumns && isDesktop ? MAX_ITEMS_DESKTOP_MULTIPLE_COLUMN : MAX_ITEMS_MOBILE;
  const [randomBackground, setRandomBackground] = useState<string[]>(
    new Array(MAX_ITEMS_DESKTOP_MULTIPLE_COLUMN).fill(''),
  );

  const displayTwoColumns = multipleColumns && previewItems && previewItems.length >= 2;
  const isEmpty = previewItems && previewItems.length === 0;
  const moreThanMaximum = previewItems && previewItems.length > displayMaximum;

  const getRandomBackground = () => {
    const colors = theme?.listPreviewCard.iconBackground;
    return colors[Math.floor(Math.random() * colors.length)];
  };

  useEffect(() => {
    setRandomBackground(randomBackground.map(getRandomBackground));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Container
        multipleColumns={multipleColumns}
        twoColumns={displayTwoColumns}
        noBorder={noBorder}
        error={!!errorMessage}
      >
        {noBorder && <Separator />}
        <Header $empty={isEmpty} twoColumns={displayTwoColumns} onClick={onAdd}>
          <Header.Icon src={`/corporate/images/list-preview-icons/${icon}${isEmpty ? '-disabled' : ''}.svg`} alt="" />
          <Header.Placeholder $empty={isEmpty}>{placeholder}</Header.Placeholder>
          <Header.ArrowIcon $empty={isEmpty} />
        </Header>
        {!isEmpty && (
          <ItemsContainer onClick={onClick}>
            <Separator />
            <ItemsPreview twoColumns={displayTwoColumns}>
              {!children && previewItems
                ? previewItems.slice(0, displayMaximum).map((item, index) => (
                    <Item key={index.toString()}>
                      <ItemIcon
                        item={item}
                        showInitials={showInitials}
                        randomBackground={randomBackground}
                        index={index}
                      />
                      <Item.Text>
                        <Item.Title>{item.title}</Item.Title>
                        {item.subtitle && <Item.Subtitle>{item.subtitle}</Item.Subtitle>}
                      </Item.Text>
                    </Item>
                  ))
                : children}
            </ItemsPreview>
            {moreThanMaximum && (
              <>
                <Separator />
                <Footer>
                  {t('listPreviewCard.seeMore', { count: previewItems && previewItems.length - displayMaximum })}
                </Footer>
              </>
            )}
          </ItemsContainer>
        )}
      </Container>
      {errorMessage && <ErrorMessage>{t(errorMessage)}</ErrorMessage>}
    </div>
  );
};

export default withTheme(ListPreviewCard);
